/**
 * Monthly management report PDF — A4 single-page executive summary.
 * Consolidates: turnover, material margin, outstanding receivables,
 * late orders, admin tasks overdue, stock shortages.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function fmtEur(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("fr-FR");
}

/**
 * @param {Object} opts
 * @param {number} opts.year
 * @param {number} opts.month 1-12
 * @param {Object} opts.stats            dashboard stats { realized_amount, pending_amount, commission_amount, ... }
 * @param {Object} opts.marginSummary    material-margins summary { total_ht, total_materials_cost, total_margin, total_margin_pct, orders_count }
 * @param {Object} opts.outstanding      outstanding-receivables { total, pending_total, completed_unpaid_total }
 * @param {Object} opts.lateOrders       { count, late_orders[] }
 * @param {Object} opts.adminTasks       dashboard-summary { overdue_count, this_week_count, high_backlog_count, overdue[], this_week[] }
 * @param {Object} opts.stockForecast    supplier stock-forecast { items[], alert_count, below_threshold_count }
 * @param {String} opts.companyName      optional tenant company name for header
 */
export function generateManagementReportPdf({
  year, month, stats, marginSummary, outstanding, lateOrders, adminTasks, stockForecast, companyName,
}) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210;
  const margin = 12;
  let y = 14;

  // Header
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text("Reporting de gestion", margin, y);
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(`${MONTHS[month - 1]} ${year}`, pw - margin, y, { align: "right" });
  y += 6;
  if (companyName) {
    pdf.setFontSize(10);
    pdf.setTextColor(120);
    pdf.text(companyName, margin, y);
    pdf.setTextColor(0);
    y += 5;
  }
  pdf.setFontSize(8);
  pdf.setTextColor(120);
  pdf.text(`Édité le ${new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`, pw - margin, y, { align: "right" });
  pdf.setTextColor(0);
  y += 6;

  pdf.setDrawColor(210);
  pdf.line(margin, y, pw - margin, y);
  y += 5;

  // Block 1 — KPIs (4 columns)
  const kpis = [
    { label: "CA réalisé", value: fmtEur(stats?.realized_amount), color: [34, 197, 94] },
    { label: "En attente", value: fmtEur(stats?.pending_amount), color: [245, 158, 11] },
    { label: "Marge matière", value: marginSummary?.total_margin_pct != null ? fmtPct(marginSummary.total_margin_pct) : "—", color: [99, 102, 241] },
    { label: "Encours (TTC)", value: fmtEur(outstanding?.total), color: [239, 68, 68] },
  ];
  const kpiW = (pw - 2 * margin) / 4;
  kpis.forEach((k, idx) => {
    const x = margin + idx * kpiW;
    pdf.setFillColor(247, 247, 250);
    pdf.roundedRect(x + 1, y, kpiW - 2, 18, 1.5, 1.5, "F");
    pdf.setFontSize(8);
    pdf.setTextColor(110);
    pdf.text(k.label, x + 3, y + 5);
    pdf.setTextColor(k.color[0], k.color[1], k.color[2]);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text(String(k.value), x + 3, y + 14);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0);
  });
  y += 22;

  // Block 2 — Marge matière details
  if (marginSummary && marginSummary.orders_count > 0) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("Marge matière (cumul)", margin, y);
    y += 4;
    autoTable(pdf, {
      startY: y,
      head: [["Commandes avec matière", "CA HT", "Coût matière", "Marge €", "Marge %"]],
      body: [[
        String(marginSummary.orders_count),
        fmtEur(marginSummary.total_ht),
        fmtEur(marginSummary.total_materials_cost),
        fmtEur(marginSummary.total_margin),
        fmtPct(marginSummary.total_margin_pct),
      ]],
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255 },
      margin: { left: margin, right: margin },
    });
    y = pdf.lastAutoTable.finalY + 4;
  }

  // Block 3 — Late orders
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text(`Commandes en retard (${lateOrders?.count || 0})`, margin, y);
  y += 4;
  if (lateOrders && lateOrders.count > 0) {
    autoTable(pdf, {
      startY: y,
      head: [["Référence", "Client", "Retard"]],
      body: lateOrders.late_orders.slice(0, 8).map(o => [
        o.reference,
        o.client_name || "—",
        o.days_late > 0 ? `${o.days_late} j` : `dépasse limite de ${o.days_late_constraint} j`,
      ]),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [239, 68, 68], textColor: 255 },
      margin: { left: margin, right: margin },
    });
    y = pdf.lastAutoTable.finalY + 4;
  } else {
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text("Aucune commande en retard.", margin, y);
    pdf.setTextColor(0);
    y += 5;
  }

  // Block 4 — Admin tasks overdue
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text(`Administratif en retard (${adminTasks?.overdue_count || 0})`, margin, y);
  y += 4;
  if (adminTasks && adminTasks.overdue_count > 0) {
    autoTable(pdf, {
      startY: y,
      head: [["Tâche", "Échéance"]],
      body: (adminTasks.overdue || []).map(o => [o.title, fmtDate(o.due_date)]),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [217, 119, 6], textColor: 255 },
      margin: { left: margin, right: margin },
    });
    y = pdf.lastAutoTable.finalY + 4;
  } else {
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text("Rien en retard côté administratif.", margin, y);
    pdf.setTextColor(0);
    y += 5;
  }

  // Block 5 — Stock shortages
  const shortages = (stockForecast?.items || []).filter(i => i.alert || i.below_threshold);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text(`Stock à surveiller (${shortages.length})`, margin, y);
  y += 4;
  if (shortages.length > 0) {
    autoTable(pdf, {
      startY: y,
      head: [["Produit", "Fournisseur", "Stock", "Conso", "À commander", "État"]],
      body: shortages.slice(0, 8).map(r => [
        r.product_name,
        r.supplier_name || "—",
        `${r.current_stock ?? 0} ${r.reference_unit || ""}`,
        `${r.upcoming_consumption ?? 0} ${r.reference_unit || ""}`,
        `${r.to_order ?? 0} ${r.reference_unit || ""}${r.suggested_order_by_date ? ` (av. ${fmtDate(r.suggested_order_by_date)})` : ""}`,
        r.alert ? "RUPTURE" : "Sous seuil",
      ]),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255 },
      margin: { left: margin, right: margin },
    });
    y = pdf.lastAutoTable.finalY + 4;
  } else {
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text("Aucun produit en rupture ou sous seuil.", margin, y);
    pdf.setTextColor(0);
    y += 5;
  }

  // Footer
  pdf.setFontSize(7);
  pdf.setTextColor(140);
  pdf.text("Généré par Bati Assistant", margin, 290);

  pdf.save(`reporting-gestion-${year}-${String(month).padStart(2, "0")}.pdf`);
}
